// The interaction state every chart shares (APS-577): the point under the pointer or the
// keyboard, the series the legend hides, the zoomed span of a time axis and the span being
// dragged. Pure, so it is tested without a DOM; useChart (./useChart.ts) drives it from events.
//
// Zoom is held as x keys, not indices: a live chart (a person's memory, APS-577) keeps a sliding
// window of samples, so an index points at a different sample once one arrives. A key stays on
// its sample; when the window slides past the zoom's start, the zoom starts at the window's.

export type XKey = string | number

export interface ChartState {
  /** The active point's key: under the pointer, or moved to with the arrows. */
  active: XKey | null
  /** Indices of the series the legend hides (0 is the first series). */
  hidden: readonly number[]
  /** The zoomed span, both ends included; null shows every point. */
  zoom: { from: XKey; to: XKey } | null
  /** The span being marked, by a drag or by Shift and the arrows; from is where it started. */
  brush: { from: XKey; to: XKey } | null
  /**
   * The series `hidden` was set for (their names, joined). Other series (a metric switched, a
   * grouping changed) start with every series shown: an index means another series now.
   */
  hiddenFor: string
  /**
   * The data the zoom and the span were set on (a chart without lasting keys passes its labels):
   * a range switched to other days starts unzoomed instead of on the same indices.
   */
  zoomFor: string
}

export const INITIAL: ChartState = {
  active: null,
  hidden: [],
  zoom: null,
  brush: null,
  hiddenFor: '',
  zoomFor: '',
}

export type ChartAction =
  | { type: 'point'; key: XKey | null }
  | { type: 'toggle'; series: number; count: number; names: string }
  | { type: 'isolate'; series: number; count: number; names: string }
  | { type: 'brushStart'; key: XKey; data: string }
  | { type: 'brushTo'; key: XKey }
  | { type: 'brushEnd'; keys: readonly XKey[] }
  | { type: 'brushCancel' }
  | { type: 'reset' }

/** The fewest points a zoom shows: a span narrower than this is a click, not a zoom. */
export const MIN_ZOOM_POINTS = 2

export function chartReducer(state: ChartState, action: ChartAction): ChartState {
  switch (action.type) {
    case 'point':
      return state.active === action.key ? state : { ...state, active: action.key }
    case 'toggle': {
      const { series, count, names } = action
      if (series < 0 || series >= count) return state
      const was = hiddenOf(state, names)
      const hidden = was.includes(series)
        ? was.filter((s) => s !== series)
        : [...was, series].sort((a, b) => a - b)
      // Hiding the last one shown shows them all again: a chart never goes blank.
      return { ...state, hidden: hidden.length >= count ? [] : hidden, hiddenFor: names }
    }
    case 'isolate': {
      const { series, count, names } = action
      if (series < 0 || series >= count) return state
      const was = hiddenOf(state, names)
      const only = Array.from({ length: count }, (_, i) => i).filter((i) => i !== series)
      // Isolating the one already isolated shows every series again.
      const already = only.length === was.length && only.every((i) => was.includes(i))
      return { ...state, hidden: already ? [] : only, hiddenFor: names }
    }
    case 'brushStart': {
      // On other data the old zoom is gone (zoomOf), so a new span starts from the whole series.
      const zoom = state.zoomFor === action.data ? state.zoom : null
      return { ...state, zoom, zoomFor: action.data, brush: { from: action.key, to: action.key } }
    }
    case 'brushTo':
      return state.brush && state.brush.to !== action.key
        ? { ...state, brush: { ...state.brush, to: action.key } }
        : state
    case 'brushEnd': {
      if (!state.brush) return state
      const span = orderedSpan(action.keys, state.brush.from, state.brush.to)
      if (!span || span[1] - span[0] + 1 < MIN_ZOOM_POINTS) return { ...state, brush: null }
      const from = action.keys[span[0]] as XKey
      const to = action.keys[span[1]] as XKey
      return { ...state, brush: null, zoom: { from, to } }
    }
    case 'brushCancel':
      return state.brush ? { ...state, brush: null } : state
    case 'reset':
      return state.zoom || state.brush ? { ...state, zoom: null, brush: null } : state
  }
}

/** The series hidden for these series (their names, joined): none when they are other series. */
export const hiddenOf = (state: ChartState, names: string): readonly number[] =>
  state.hiddenFor === names ? state.hidden : []

/** The zoom and the span being marked, on this data; none when set on other data. */
export const zoomOf = (state: ChartState, data: string): Pick<ChartState, 'zoom' | 'brush'> =>
  state.zoomFor === data ? { zoom: state.zoom, brush: state.brush } : { zoom: null, brush: null }

/** Two keys' indices in order, or null when either is not among the keys. */
export function orderedSpan(
  keys: readonly XKey[],
  a: XKey,
  b: XKey,
): readonly [number, number] | null {
  const i = keys.indexOf(a)
  const j = keys.indexOf(b)
  if (i < 0 || j < 0) return null
  return i <= j ? [i, j] : [j, i]
}

/**
 * The indices a zoom shows today, both ends included. A start the window has slid past starts
 * at the window's first point; an end that is gone, or a span of fewer than two points left,
 * shows everything (the zoomed samples have all left the window).
 */
export function zoomWindow(
  keys: readonly XKey[],
  zoom: ChartState['zoom'],
): readonly [number, number] {
  const all = [0, Math.max(0, keys.length - 1)] as const
  if (!zoom) return all
  const to = keys.indexOf(zoom.to)
  if (to < 0) return all
  const at = keys.indexOf(zoom.from)
  const from = at < 0 ? 0 : at
  if (to - from + 1 < MIN_ZOOM_POINTS) return all
  return [from, to]
}

/** The index of the x nearest `px` among increasing positions (binary search). */
export function nearestIndex(xs: readonly number[], px: number): number {
  const n = xs.length
  if (n === 0) return -1
  let lo = 0
  let hi = n - 1
  while (hi - lo > 1) {
    const mid = (lo + hi) >> 1
    if ((xs[mid] as number) <= px) lo = mid
    else hi = mid
  }
  const a = xs[lo] as number
  const b = xs[hi] as number
  return Math.abs(px - a) <= Math.abs(b - px) ? lo : hi
}

/**
 * Where the arrows, Home and End take the active point; null: the key is not the chart's (Up
 * and Down stay the page's scroll).
 */
export function keyStep(key: string, at: number, count: number): number | null {
  if (count === 0) return null
  switch (key) {
    case 'ArrowRight':
      return at < 0 ? count - 1 : Math.min(count - 1, at + 1)
    case 'ArrowLeft':
      return at < 0 ? count - 1 : Math.max(0, at - 1)
    case 'Home':
      return 0
    case 'End':
      return count - 1
    default:
      return null
  }
}

/**
 * Whether a touch drag over the chart should zoom. Inside a region that scrolls sideways (a wide
 * table's `.scroll-x`, a card on a phone), a sideways drag is that region's scroll: zoom on touch
 * stays off there, and a tap still shows the tooltip (APS-577, the lead's note).
 */
export function inSidewaysScroller(el: Element | null): boolean {
  for (let node = el?.parentElement ?? null; node; node = node.parentElement) {
    if (node === document.body || node === document.documentElement) return false
    const { overflowX } = getComputedStyle(node)
    if ((overflowX === 'auto' || overflowX === 'scroll') && node.scrollWidth > node.clientWidth)
      return true
  }
  return false
}
